import React, { useState, useCallback, useMemo } from "react";
import {
  FormContainerItem,
  SecondaryButton,
  Table,
  TableBody,
  TableHeader,
  TableRow,
  Td,
  TextButton,
  Th,
  InformationBanner,
  TitleWrap,
  ClusterWrap,
  SecondaryStatusTag,
  WarningBanner,
} from "@graffer-inc/console-design-kit";
import {
  AssignmentRule,
  TargetField,
} from "../../../../../application/domain/procedure-assignment-setting-ruleset/procedure-assignment-setting-ruleset";
import { RuleModal } from "./ruleModal";
import { GroupMap } from "../../../../../application/domain/group/group";

interface RulesProps {
  rules: AssignmentRule[];
  targetFields: TargetField[];
  groupMaps: GroupMap;
  handleDeleteRule: (index: number) => void;
  handleSaveRule: (rule: AssignmentRule, index?: number) => void;
  errorMessage?: string;
}

export const Rules: React.FC<RulesProps> = ({
  rules,
  targetFields,
  groupMaps,
  handleDeleteRule,
  handleSaveRule,
  errorMessage,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | undefined>(
    undefined,
  );

  const allMatchConsts = useMemo(() => {
    return rules.flatMap((rule) =>
      rule.conditions.map((condition) => condition.match.const),
    );
  }, [rules]);

  const renderConditionElement = (rule: AssignmentRule): React.ReactNode => {
    if (rule.conditions.length === 0) {
      return (
        <>
          <SecondaryStatusTag type="PRIMARY">無条件</SecondaryStatusTag>
          <span> 無条件で振り分けられます </span>
        </>
      );
    }
    return (
      <ul>
        {rule.conditions.map((condition, index) => {
          const field = targetFields.find((f) => f.elemId === condition.id);
          return field ? (
            <li key={index} style={{ display: "inline-block" }}>
              <SecondaryStatusTag type="PRIMARY">{`${field.description}(${condition.id})`}</SecondaryStatusTag>
              <span> が </span>
              <SecondaryStatusTag type="PRIMARY">{`${condition.match.const}`}</SecondaryStatusTag>
              {index < rule.conditions.length - 1 && " かつ "}
            </li>
          ) : null;
        })}
      </ul>
    );
  };

  const generateAssignmentText = (rule: AssignmentRule): string => {
    if (Object.keys(groupMaps).length === 0) {
      return "";
    }
    return rule.assignments
      .map((assignment) => {
        const group = groupMaps[assignment.assignee.id];
        return `グループ: ${group.name}`;
      })
      .join(", ");
  };

  const findMissingExpectedValues = (targetField: TargetField): string[] => {
    const missingValues = targetField.expectedValues.filter(
      (value) => !allMatchConsts.includes(value),
    );
    return missingValues;
  };

  const openAddModal = useCallback(() => {
    setEditingRuleIndex(undefined);
    setIsModalOpen(true);
  }, [setEditingRuleIndex, setIsModalOpen]);

  const openEditModal = useCallback(
    (index: number) => {
      setEditingRuleIndex(index);
      setIsModalOpen(true);
    },
    [setEditingRuleIndex, setIsModalOpen],
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const handleSaveModal = useCallback(
    (rule: AssignmentRule) => {
      handleSaveRule(rule, editingRuleIndex);
      setIsModalOpen(false);
    },
    [handleSaveRule, editingRuleIndex, setIsModalOpen],
  );

  return (
    <>
      <FormContainerItem>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <TitleWrap
              id="rules-title"
              title="振り分けルール一覧"
              description="振り分ける「条件」と「振り分け先」を設定して下さい。"
              required
            />
          </div>
          <SecondaryButton
            text="振り分けルール追加"
            onClick={openAddModal}
            icon="PlusCircleIcon"
          />
        </div>
      </FormContainerItem>

      <FormContainerItem>
        {!errorMessage && rules.length === 0 && (
          <div className="py-2">
            <InformationBanner
              message="振り分けルールがまだ登録されていません。"
              description="「振り分けルール追加」より、振り分けルールを追加してください。"
            />
          </div>
        )}
        {errorMessage && (
          <div className="py-2">
            <WarningBanner message={errorMessage} />
          </div>
        )}
        {rules.length > 0 &&
          targetFields.length > 0 &&
          targetFields.map((targetField) => {
            const missingValues = findMissingExpectedValues(targetField);
            if (missingValues.length > 0) {
              return (
                <div key={targetField.elemId} className="py-2">
                  <WarningBanner
                    message={`「${targetField.description}」の期待される選択肢[${missingValues?.join(", ")}]がルールの条件に設定されていません。`}
                  />
                </div>
              );
            }
            return null;
          })}
        {rules.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <Th>No.</Th>
                <Th>条件</Th>
                <Th>振り分け先</Th>
                <Th>アクション</Th>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((_, index) => {
                const reversedIndex = rules.length - 1 - index;
                return (
                  <TableRow key={reversedIndex}>
                    <Td>{reversedIndex + 1}</Td>
                    <Td>{renderConditionElement(rules[reversedIndex])}</Td>
                    <Td>{generateAssignmentText(rules[reversedIndex])}</Td>
                    <Td>
                      <ClusterWrap>
                        <TextButton
                          text="編集"
                          icon="EditIcon"
                          onClick={() => openEditModal(reversedIndex)}
                        />
                        <TextButton
                          text="削除"
                          color="danger"
                          icon="TrashIcon"
                          onClick={() => handleDeleteRule(reversedIndex)}
                        />
                      </ClusterWrap>
                    </Td>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </FormContainerItem>

      <RuleModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveModal}
        initialData={
          editingRuleIndex !== undefined ? rules[editingRuleIndex] : undefined
        }
        targetFields={targetFields}
      />
    </>
  );
};
